import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { getSocket as getOprSocket } from 'src/creators/OpenReporting.creators';
import { getSocket } from 'src/creators/User.creators';
import {
  GetChannelEntriesQuery,
  GetMessagesByReportQuery,
  GetUserChannelEntriesQuery,
  GetUserMessagesByReportQuery,
} from 'src/graphql/generated';
import { Message, MessageState, State } from 'src/types';

type ChannelEntrySocketPayload = {
  id: number;
  channel: string;
  manager: number;
  enteredChannel?: string | null;
  exitedChannel?: string | null;
  User: { name?: string | null; displayName?: string | null };
};

type MessageQueryData = GetMessagesByReportQuery | GetUserMessagesByReportQuery;

type MessageArray = GetMessagesByReportQuery['getMessagesByReport'];

function getMessages(data: MessageQueryData): MessageArray {
  if ('getMessagesByReport' in data) {
    return data.getMessagesByReport;
  }
  return (data as GetUserMessagesByReportQuery).getUserMessagesByReport;
}

function setMessages(
  data: MessageQueryData,
  messages: NonNullable<GetMessagesByReportQuery['getMessagesByReport']>,
): MessageQueryData {
  if ('getMessagesByReport' in data) {
    return { ...data, getMessagesByReport: messages };
  }
  return {
    ...data,
    getUserMessagesByReport: messages,
  } as GetUserMessagesByReportQuery;
}

interface UseSocketMessagesOptions {
  channel: string | null;
  translate: boolean;
  queryKey: 'GetMessagesByReport' | 'GetUserMessagesByReport';
  context?: 'reshub' | 'opr';
}

export const useSocketMessages = ({
  channel,
  translate,
  queryKey,
  context = 'reshub',
}: UseSocketMessagesOptions) => {
  const queryClient = useQueryClient();
  const reshubSession = useSelector(
    (state: State) => state.globalUsers.session,
  );
  const oprSession = useSelector(
    (state: State) => state.openReporting?.openReportingReducer?.session,
  );
  const session = context === 'opr' ? oprSession : reshubSession;

  const channelRef = useRef(channel);
  channelRef.current = channel;

  const translateRef = useRef(translate);
  translateRef.current = translate;

  useEffect(() => {
    if (!session) {
      return;
    }

    const socket =
      context === 'opr' ? getOprSocket(session) : getSocket(session);

    const handleChat = (message: Message) => {
      if (!channelRef.current || message.channel !== channelRef.current) {
        return;
      }

      const cacheKey = [
        queryKey,
        { report: channelRef.current, translate: translateRef.current },
      ];

      const current = queryClient.getQueryData<MessageQueryData>(cacheKey);
      if (!current) {
        queryClient.invalidateQueries(cacheKey);
        return;
      }

      const existing = getMessages(current) ?? [];
      const filtered = existing.filter((m) => m.id !== message.id);
      const newMessage = {
        __typename: 'ResponseMessageType' as const,
        id: message.id,
        value: message.value,
        state: message.state,
        channel: message.channel,
        author: message.author,
        isReporter: message.isReporter,
        createdAt: message.createdAt,
        updatedAt: message.updatedAt,
        translated: message.translated ?? false,
        deletedAt: message.deletedAt ?? null,
        deletedBy: message.deletedBy ?? null,
      };

      queryClient.setQueryData(
        cacheKey,
        setMessages(current, [...filtered, newMessage]),
      );
    };

    const handleReadMessages = (messages: Message[]) => {
      if (!channelRef.current) {
        return;
      }

      const relevantMessages = messages.filter(
        (m) => m.channel === channelRef.current,
      );

      if (relevantMessages.length === 0) {
        return;
      }

      const cacheKey = [
        queryKey,
        { report: channelRef.current, translate: translateRef.current },
      ];

      const current = queryClient.getQueryData<MessageQueryData>(cacheKey);
      if (!current) {
        return;
      }

      const existing = getMessages(current) ?? [];
      const readIds = new Set(relevantMessages.map((m) => m.id));
      const updated = existing.map((m) =>
        readIds.has(m.id) ? { ...m, state: MessageState.READ } : m,
      );

      queryClient.setQueryData(cacheKey, setMessages(current, updated));
    };

    const handleChannelEntry = (entry: ChannelEntrySocketPayload) => {
      if (entry.channel !== channelRef.current) {
        return;
      }

      const channelEntriesKey =
        context === 'opr'
          ? ['GetUserChannelEntries', { channel: entry.channel }]
          : ['GetChannelEntries', { channel: entry.channel }];

      const existing = queryClient.getQueryData<
        GetChannelEntriesQuery | GetUserChannelEntriesQuery
      >(channelEntriesKey);

      if (!existing) {
        queryClient.invalidateQueries(channelEntriesKey);
        return;
      }

      type ChannelEntryItem = NonNullable<
        GetChannelEntriesQuery['getChannelEntries']
      >[number];

      const mappedEntry: ChannelEntryItem = {
        __typename: 'ManagedChannelType',
        id: entry.id,
        manager: entry.manager,
        enteredChannel: entry.enteredChannel,
        exitedChannel: entry.exitedChannel,
        User: {
          __typename: 'UserType',
          name: entry.User?.name ?? null,
          displayName: entry.User?.displayName ?? null,
        },
      };

      const currentEntries: ChannelEntryItem[] =
        ('getChannelEntries' in existing
          ? existing.getChannelEntries
          : (existing as GetUserChannelEntriesQuery).getUserChannelEntries) ??
        [];

      const filtered = currentEntries.filter((e) => e.id !== entry.id);
      const updated = [...filtered, mappedEntry];

      if ('getChannelEntries' in existing) {
        queryClient.setQueryData(channelEntriesKey, {
          ...existing,
          getChannelEntries: updated,
        });
      } else {
        queryClient.setQueryData(channelEntriesKey, {
          ...existing,
          getUserChannelEntries: updated,
        });
      }
    };

    socket.on('chat', handleChat);
    socket.on('chat-read-messages', handleReadMessages);
    socket.on('channel-entry', handleChannelEntry);

    return () => {
      socket.off('chat', handleChat);
      socket.off('chat-read-messages', handleReadMessages);
      socket.off('channel-entry', handleChannelEntry);
    };
  }, [session, queryClient, queryKey, context]);
};
